import { useEffect, useRef, useState } from 'react';
import { invoiceScanApi, type ScanOrigin } from '../api/services';
import type { InvoiceScanResultDto, InvoiceScanStatusDto } from '../api/types';
import { Alert, Badge, Button, Modal, Spinner, errorMessage, useAction } from './components';

/**
 * v4.3 — Lectura de facturas con IA.
 *   ScanButton: toma la foto (o elige una imagen / PDF), la reduce en el navegador y la envía a leer.
 *   ScanSummary: muestra la foto, la seguridad de la lectura y los avisos, para que el usuario revise antes de guardar.
 *   AttachmentViewer: muestra la foto guardada de una factura ya registrada.
 */

let statusPromise: Promise<InvoiceScanStatusDto> | null = null;
/** El estado se pide una sola vez por sesión; si falla se considera deshabilitado (el botón no aparece). */
export function useScanStatus(): InvoiceScanStatusDto | null {
  const [s, setS] = useState<InvoiceScanStatusDto | null>(null);
  useEffect(() => {
    let alive = true;
    statusPromise ??= invoiceScanApi.status().catch(() => ({ enabled: false, maxFileMb: 0 }));
    statusPromise.then((v) => { if (alive) setS(v); });
    return () => { alive = false; };
  }, []);
  return s;
}

const MAX_SIDE = 2000;

/** Reduce la foto a 2000 px por lado (JPEG): basta para leer la factura y pesa mucho menos. Los PDF se envían tal cual. */
async function prepare(file: File): Promise<{ contentType: string; base64: string; previewUrl: string | null; fileName: string }> {
  if (file.type === 'application/pdf') {
    const buf = new Uint8Array(await file.arrayBuffer());
    let bin = '';
    for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
    return { contentType: 'application/pdf', base64: btoa(bin), previewUrl: null, fileName: file.name || 'factura.pdf' };
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('No se pudo abrir la imagen. Use una foto JPG o PNG.'));
      i.src = url;
    });
    const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const g = canvas.getContext('2d');
    if (!g) throw new Error('El navegador no permite procesar la imagen.');
    g.fillStyle = '#fff';
    g.fillRect(0, 0, canvas.width, canvas.height);
    g.drawImage(img, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    const name = (file.name || 'factura').replace(/\.[^.]+$/, '') + '.jpg';
    return { contentType: 'image/jpeg', base64: dataUrl.slice(dataUrl.indexOf(',') + 1), previewUrl: dataUrl, fileName: name };
  } finally {
    URL.revokeObjectURL(url);
  }
}

export interface ScanOutcome { result: InvoiceScanResultDto; previewUrl: string | null }

export function ScanButton({ origin, fundId, onResult, label = 'Leer factura con foto' }: { origin: ScanOrigin; fundId?: number | null; onResult: (o: ScanOutcome) => void; label?: string }) {
  const status = useScanStatus();
  const input = useRef<HTMLInputElement>(null);
  const { run, busy } = useAction();
  if (!status?.enabled) return null;
  const pick = (file: File | undefined) => {
    if (!file) return;
    void run(async () => {
      const p = await prepare(file);
      if (p.base64.length * 0.75 > status.maxFileMb * 1024 * 1024) throw new Error(`El archivo pesa más de ${status.maxFileMb} MB.`);
      const result = await invoiceScanApi.scan(origin, { fileName: p.fileName, contentType: p.contentType, dataBase64: p.base64, fundId: fundId ?? null });
      onResult({ result, previewUrl: p.previewUrl });
    }, { success: 'Factura leída: revise los datos antes de guardar.' });
  };
  return (
    <>
      <input ref={input} type="file" accept="image/*,application/pdf" hidden
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
      <Button busy={busy} onClick={() => input.current?.click()} title="Tome una foto de la factura o elija una imagen o PDF">{busy ? 'Leyendo factura…' : `📷 ${label}`}</Button>
    </>
  );
}

const CONF: Record<string, { tone: 'ok' | 'warn' | 'bad'; text: string }> = {
  alta: { tone: 'ok', text: 'Lectura clara' }, media: { tone: 'warn', text: 'Lectura con dudas' }, baja: { tone: 'bad', text: 'Lectura poco segura' },
};

/** Resumen de lo leído: foto, seguridad y avisos. Los datos ya quedaron precargados en el formulario. */
export function ScanSummary({ outcome, onDiscard }: { outcome: ScanOutcome; onDiscard: () => void }) {
  const [big, setBig] = useState(false);
  const c = CONF[outcome.result.confidence] ?? CONF.media;
  return (
    <div className="acc-scan">
      {outcome.previewUrl
        ? <button type="button" className="acc-scan-thumb" onClick={() => setBig(true)} title="Ver la foto en grande"><img src={outcome.previewUrl} alt="Foto de la factura" /></button>
        : <div className="acc-scan-thumb acc-scan-pdf">PDF</div>}
      <div className="acc-scan-body">
        <div><Badge tone={c.tone}>{c.text}</Badge> <span className="acc-muted">Datos precargados por IA: revíselos contra la foto antes de guardar.</span></div>
        {outcome.result.warnings.length > 0 && (
          <Alert kind={outcome.result.warnings.some((w) => w.includes('YA está registrada')) ? 'error' : 'warn'}>
            <ul className="acc-scan-list">{outcome.result.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
          </Alert>
        )}
        <Button kind="link" onClick={onDiscard}>Quitar la foto</Button>
      </div>
      {big && outcome.previewUrl && (
        <Modal title="Foto de la factura" onClose={() => setBig(false)} size="lg">
          <img className="acc-scan-big" src={outcome.previewUrl} alt="Foto de la factura" />
        </Modal>
      )}
    </div>
  );
}

/** Foto o PDF guardado de una factura ya registrada. */
export function AttachmentViewer({ origin, reference, title, onClose }: { origin: ScanOrigin; reference: string | number; title: string; onClose: () => void }) {
  const [file, setFile] = useState<{ url: string; type: string } | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    let alive = true;
    let url: string | null = null;
    invoiceScanApi.file(origin, reference).then((f) => { url = f.url; if (alive) setFile(f); else URL.revokeObjectURL(f.url); }).catch((e) => { if (alive) setError(e); });
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [origin, reference]);
  return (
    <Modal title={title} onClose={onClose} size="lg">
      {error ? <Alert kind="error">{errorMessage(error)}</Alert>
        : !file ? <Spinner />
          : file.type.includes('pdf') ? <iframe className="acc-scan-pdfview" src={file.url} title={title} />
            : <img className="acc-scan-big" src={file.url} alt={title} />}
      <div className="acc-actions">
        {file && <a className="acc-btn" href={file.url} download={`factura-${reference}${file.type.includes('pdf') ? '.pdf' : '.jpg'}`}>Descargar</a>}
        <Button onClick={onClose}>Cerrar</Button>
      </div>
    </Modal>
  );
}
